import { 
  Box, 
  Heading, 
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  useBreakpointValue,
  SimpleGrid,
  Text,
  Flex,
  Stack,
  Skeleton
} from "@chakra-ui/react"
import { LargeConnect } from "../Connect/LargeConnect";
import { useConnectWallet } from '@web3-onboard/react';
import { ethers } from "@web3-onboard/common/node_modules/ethers";
import { useState, useEffect, useMemo } from "react";
import { useExternalProvider } from "../../hooks/useExternalProvider";
import { useSigner } from "../../hooks/useSigner";
import { useAccount } from "../../hooks/useAccount";
import { useBUSDPrice } from "../../hooks/useBUSDPrice";
import { infoToast, errorToast, successToast } from "../Toasts/Toasts";
import { dollarValue, numberFormatter } from "../../utils/formatters";

const tokenContract = "0xa27781A1B162dEdD3eFcD340AD8C6308996fe5fb";
const pancakeRouterAddress = "0x10ED43C718714eb63d5aA57B78B54704E256024E";
const BUSD = "0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56";
const WBNB = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";

const tokenAbi = [
  "function balanceOf(address) view returns(uint256)",
  "function getUnpaidDividend(address shareholder) public view returns (uint256)",
  "function getPaidDividend(address shareholder) public view returns (uint256)",
  "function claimDividend() external",
]

const pancakeAbi = [
  "function getAmountsOut(uint amountIn, address[] memory path) view returns (uint[] amounts)"
]

export const Rewards = () => {
  const { busdPrice } = useBUSDPrice();
  const { signer } = useSigner();
  const { externalProvider } = useExternalProvider();
  const { account } = useAccount();
  const [{ wallet }] = useConnectWallet();
  const pancakeInstance = useMemo(() => new ethers.Contract(pancakeRouterAddress, pancakeAbi, externalProvider), [externalProvider]);
  const tokenReadInstance = useMemo(() => new ethers.Contract(tokenContract, tokenAbi, externalProvider), [externalProvider]);
  const tokenWriteInstance = useMemo(() => new ethers.Contract(tokenContract, tokenAbi, signer), [signer]);
  
  const [address, setAddress] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  const [stackdBalance, setStackdBalance] = useState(0);

  const [busdValue, setBusdValue] = useState("$0.00");
  const [busdRewardsEarned, setBusdRewardsEarned] = useState("$0.00");
  const [busdUnclaimed, setBusdUnclaimed] = useState("$0.00");
  
  const isDesktop = useBreakpointValue({base: false, lg: true, });
  const valueFont = isDesktop ? "4xl" : "2xl";
  const descriptionFont = isDesktop ? "3xl" : "lg";

  const isError = !ethers.utils.isAddress(address);
  const handleAddressInput = e =>{
    let input = e.target.value;
    setAddress(input);
  };

  const _calculateBUSDPrice = async (stackdAmount) => {
    const amountOut = await pancakeInstance.getAmountsOut(stackdAmount, [tokenContract, WBNB]);
    const amount = await pancakeInstance.getAmountsOut(amountOut[1], [WBNB, BUSD]);
    return amount[1].toString();
  }

  const _calculateBUSDPriceFromApiPrice = async (busdAmount) => {
    const stringPrice = busdPrice.toString().split(".");
    let price;
    let power;
    if(stringPrice[1] === undefined){
      price = stringPrice[0];
      power = 10 ** (price.length - 1)
    }else if(stringPrice[0] === "0"){
      price = stringPrice[1];
      power = 10 ** price.length
    } else {
      price = stringPrice[0] + stringPrice[1];
      power = 10 ** (price.length - 1)
    }
    const priceInWei = ethers.BigNumber.from(busdAmount).mul(Number(price)).div(power)
    return priceInWei;
  }

  const handleBalance = async (walletInput) => {
    try {
      if(!wallet && walletInput === undefined){ 
        setStackdBalance(0);
        setBusdValue("$0.00")
        return;
      }
      const amount = await tokenReadInstance.balanceOf(walletInput && !isError ? walletInput : account);
      const stackdAmount = ethers.utils.formatEther(amount);
      setStackdBalance(numberFormatter.format(stackdAmount))
      let busdAmount;
      if(amount > 0){ 
        busdAmount = await _calculateBUSDPrice(amount);
      }else {
        setBusdValue("$0.00");
        return;
      }      
      busdAmount = ethers.utils.formatEther(busdAmount);
      setBusdValue(dollarValue.format(busdAmount));
    } catch (error) {
      setIsSearching(false);
    }
  }

  const handleEarned = async (walletInput) => {
    try {
      if(!wallet && walletInput === undefined){ 
        setBusdRewardsEarned("$0.00");
        return;
      }
      const unclaimed = await tokenReadInstance.getUnpaidDividend(walletInput && !isError ? walletInput : account);
      const claimed = await tokenReadInstance.getPaidDividend(walletInput && !isError ? walletInput : account);
      const total = ethers.BigNumber.from(unclaimed).add(ethers.BigNumber.from(claimed));
      let busdAmount;
      if(total > 0){ 
        busdAmount = await _calculateBUSDPriceFromApiPrice(total);
      }else {
        setBusdRewardsEarned("$0.00");
        return;
      }
      busdAmount = ethers.utils.formatEther(busdAmount);
      setBusdRewardsEarned(dollarValue.format(busdAmount));
    } catch (error) {
      setIsSearching(false);
    }
  }

  const handleUnclaimed = async (walletInput) => {
    try {
      if(!wallet && walletInput === undefined){ 
        setBusdUnclaimed("$0.00")
        return;
      }
      const amount = await tokenReadInstance.getUnpaidDividend(walletInput && !isError ? walletInput : account);
      let busdAmount;
      if(amount > 0){ 
        busdAmount = await _calculateBUSDPriceFromApiPrice(amount);
      }else {
        setBusdUnclaimed("$0.00");
        return;
      }
      busdAmount = ethers.utils.formatEther(busdAmount);
      setBusdUnclaimed(dollarValue.format(busdAmount));
    } catch (error) {
      setIsSearching(false);
    }
  }

  const handleLoadData = async () => {
    setIsSearching(true) 
    await handleBalance();
    await handleEarned(); 
    await handleUnclaimed();
    setIsSearching(false);
  }

  useEffect(() => {
    if(tokenReadInstance && externalProvider && pancakeInstance && account && busdPrice) handleLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenReadInstance, externalProvider, pancakeInstance, isError, wallet, account, busdPrice])

  const handleGetShares = async () => {
    try {
      setIsSearching(true);
      await handleEarned(address)
      await handleUnclaimed(address)
      await handleBalance(address)
      setIsSearching(false);
    } catch (error) {
      console.log(error)
    }
  }

  const handleClaim = async () => {
    try {
      const claim = await tokenWriteInstance.claimDividend({from: wallet.accounts[0].address});
      infoToast("Initiated", "Claim being processed.");
      const result = await claim.wait();
      if(result.status === 1){
        successToast("Success", "Successfully claimed dividends.");
        await handleLoadData();
      }
    } catch (error) {
      errorToast("Error Claiming", error.message);
    }
  }

  const claimBtnDisplay = !isError && account && address.toLowerCase() !== account.toLowerCase() ?
                          null
                          :
                          <Button onClick={handleClaim} disabled={busdUnclaimed === "$0.00"} colorScheme="yellow" color="white" size="lg" w="sm" fontSize="3xl">
                            Claim   
                         </Button>;

  return (
    <Box pb={{base: 10, md: 0}} px={{base: 0, md: 5, lg: 20}}>

      <Heading>Rewards Tracker</Heading>

      <Stack spacing="1">
        <FormControl mt={7} id="walletAddress">
          <FormLabel>Wallet Address</FormLabel>
          <HStack>
            <Input
              onChange={handleAddressInput}
              maxW="md"
              borderColor="#E1B752"
              bg="#313239"
              _focus={{color: "#FFDA81"}}
              placeholder="Check rewards without connecting"
              _placeholder={{ color: 'gray.500' }}
              type="text"
              />
            <Button onClick={handleGetShares} disabled={isError} colorScheme="yellow" color="white">Search</Button>
          </HStack>
        </FormControl>
        {isError && address !== "" ? 
          <Text color="red.500">Enter valid wallet address</Text> 
          : 
          null
        }
      </Stack>


      <SimpleGrid mt={isDesktop ? 20 : 10} columns={{base: 1, md: 2, lg: 2, xl: 2}}>
        <Flex 
          direction="column"
          justifyContent="end" 
          p={5} 
          borderBottom={{base: "2px solid #FFDA81"}}
        >
          <Skeleton isLoaded={!isSearching}>
            <Text fontSize={valueFont} color="#E1B752" fontWeight="600">{stackdBalance}</Text>
          </Skeleton>
          <Text fontSize={descriptionFont} fontWeight="700">Balance (STACKD)</Text>
        </Flex>
        <Flex 
          direction="column" 
          p={5}
          borderBottom={{base: "2px solid #FFDA81"}}
          borderLeft={{base: "transparent", md: "2px solid #FFDA81"}}
        >
          <Skeleton isLoaded={!isSearching}>
            <Text fontSize={valueFont} color="#E1B752" fontWeight="600">{busdValue}</Text>
          </Skeleton>
          <Text fontSize={descriptionFont} fontWeight="700">Value (BUSD)</Text>
        </Flex>
        <Flex 
          direction="column" 
          p={5}
          borderBottom={{base: "2px solid #FFDA81", md: "transparent",}}
        >
          <Skeleton isLoaded={!isSearching}>
            <Text fontSize={valueFont} fontWeight="600">{busdRewardsEarned}</Text>
          </Skeleton>
          <Text fontSize={descriptionFont} fontWeight="700">Rewards Earned (BUSD)</Text>
        </Flex>
        <Flex 
          direction="column" 
          p={5}
          borderLeft={{base: "", md: "2px solid #FFDA81"}}
        >
          <Skeleton isLoaded={!isSearching}>
            <Text fontSize={valueFont} fontWeight="600">{busdUnclaimed}</Text>
          </Skeleton>
          <Text fontSize={descriptionFont} fontWeight="700">Unclaimed (BUSD)</Text>
        </Flex>
      </SimpleGrid>
      <Flex mt={isDesktop ? 20 : 10} justifyContent="center">
        {wallet ?
          claimBtnDisplay
          :
          <LargeConnect />
        }
      </Flex>
    </Box>
  )
}